import { useCallback, useState, useEffect } from "react";
import {
  Button,
  Group,
  TextInput,
  Select,
  Radio,
  Alert,
  Text,
  Modal,
  Card,
  Badge,
  Stack,
  Loader,
  ActionIcon,
  CopyButton,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconCheck,
  IconCopy,
  IconInfoCircle,
  IconAlertCircle,
  IconPlane,
} from "@tabler/icons-react";
import "react-phone-input-2/lib/style.css";
import {
  getPPRPricingByType, // Updated to use new database-driven function
  getAllPPRPricing, // NEW: Get all pricing options
  requestLoggedInFlight,
} from "../misc/api";
import { validateEmail } from "../misc/functions";

export const RequestFlightScreen = () => {
  const [loading, setLoading] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [request, setRequest] = useState({
    aircraft_registration: "",
    aircraft_type: "",
    weight_category: "",
    selected_pricing_id: null,
    flight_type: "single_day",
    purpose_of_flight: "PRIVATE",
    flight_date: null,
    departure_place: "",
    arrival_place: "",
    ppr_type: "one_time",
    payment_method: "online",
  });
  const [flightPurposes, setFlightPurposes] = useState([]);
  const [flightTypeOptions, setFlightTypeOptions] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [pprResult, setPprResult] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load flight type options from backend
      const pricingResponse = await getAllPPRPricing();
      const allPricing = pricingResponse?.data || [];
      
      // Filter for one_time PPR types only
      const oneTimePricing = allPricing.filter(p => p.ppr_type === 'one_time' && p.is_active);
      setFlightTypeOptions(oneTimePricing);
    } catch (error) {
      console.error("Error loading pricing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFlightPurposes = async () => {
    try {
      // const purposes = await getFlightPurposes();
      // setFlightPurposes(purposes?.data || []);
    } catch (error) {
      console.error("Error loading flight purposes:", error);
    }
  };

  const handleInputChange = useCallback((fieldName, newValue) => {
    setRequest((prev) => ({
      ...prev,
      [fieldName]: newValue,
    }));
  }, []);

  // Pricing is set directly when user selects a flight type radio option

  const validateRequest = () => {
    const required = [
      "aircraft_registration",
      "aircraft_type",
      "weight_category",
      "flight_type",
      "flight_date",
    ];

    for (const field of required) {
      if (!request[field]) {
        alert(`${field.replace("_", " ")} is required`);
        return false;
      }
    }

    return true;
  };

  const submitFlightRequest = async () => {
    if (!validateRequest()) return;

    setLoading(true);
    try {
      const requestData = {
        ...request,
        pricing_id: pricing?.pricing_id,
        pricing_amount: pricing?.price || 0,
        currency: pricing?.currency || "EUR",
        requires_payment: pricing?.requires_payment !== false,
        flight_date:
          request.flight_date?.toISOString?.() || request.flight_date,
      };

      const response = await requestLoggedInFlight(requestData);

      if (response.error) {
        alert("An error occurred! Please try again in a bit");
        console.error("Flight request error:", response.error);
      } else {
        // Handle both response.data and direct response
        const resultData = response.data || response;
        setPprResult(resultData);
        setShowSuccess(true);
      }
    } catch (error) {
      console.error("Flight request error:", error);
      alert("An error occurred! Please try again in a bit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing="xl" style={{ padding: 20 }}>
      <Text size="xl" weight={700}>
        Request Flight Permission (PPR)
      </Text>

      {/* Aircraft Information */}
      <Card withBorder padding="lg">
        <Stack spacing="md">
          <Text size="lg" weight={500}>
            Aircraft Information
          </Text>

          <TextInput
            label="Aircraft Registration"
            placeholder="e.g., YL-ABC"
            value={request.aircraft_registration}
            onChange={(e) =>
              handleInputChange(
                "aircraft_registration",
                e.target.value.toUpperCase()
              )
            }
            required
          />

          <TextInput
            label="Aircraft Type"
            placeholder="e.g., Cessna 172"
            value={request.aircraft_type}
            onChange={(e) => handleInputChange("aircraft_type", e.target.value)}
            required
          />

          <Select
            label="Aircraft Weight (MTOM)"
            placeholder="Select weight category"
            value={request.weight_category}
            onChange={(value) => handleInputChange("weight_category", value)}
            data={[
              { value: "up_to_600", label: "Up to 600 kg" },
              { value: "601_to_2400", label: "601 – 2400 kg" },
              { value: "above_2400", label: "More than 2400 kg" },
            ]}
            required
          />
        </Stack>
      </Card>

      {/* Flight Details */}
      <Card withBorder padding="lg">
        <Stack spacing="md">
          <Text size="lg" weight={500}>
            Flight Details
          </Text>

          <Radio.Group
            label="Flight Type"
            value={request.selected_pricing_id ? String(request.selected_pricing_id) : ""}
            onChange={(value) => {
              const selected = flightTypeOptions.find(o => String(o.pricing_id) === value);
              if (selected) {
                handleInputChange("selected_pricing_id", selected.pricing_id);
                handleInputChange("flight_type", selected.flight_type);
                setPricing({
                  pricing_id: selected.pricing_id,
                  pricing_code: selected.pricing_code,
                  product: selected.ppr_type,
                  flight_type: selected.flight_type,
                  price: parseFloat(selected.price),
                  currency: selected.currency || 'EUR',
                  requires_payment: selected.requires_payment,
                  description: selected.description,
                });
                setPricingError(null);
              }
            }}
            required
          >
            <Stack spacing="xs">
              {flightTypeOptions.length > 0 ? (
                flightTypeOptions.map((option) => (
                  <Radio
                    key={option.pricing_id}
                    value={String(option.pricing_id)}
                    label={
                      <Group position="apart" style={{ width: '100%' }}>
                        <div>
                          <Text weight={500}>{option.pricing_name}</Text>
                          {option.description && (
                            <Text size="xs" color="dimmed">{option.description}</Text>
                          )}
                        </div>
                        <Badge color={option.price === 0 ? 'green' : 'blue'}>
                          {option.price === 0 ? 'FREE' : `€${option.price}`}
                        </Badge>
                      </Group>
                    }
                  />
                ))
              ) : (
                <Alert icon={<IconAlertCircle />} color="yellow">
                  Loading flight type options...
                </Alert>
              )}
            </Stack>
          </Radio.Group>

          {flightPurposes.length > 0 && (
            <Select
              label="Purpose of Flight"
              value={request.purpose_of_flight}
              onChange={(value) =>
                handleInputChange("purpose_of_flight", value)
              }
              data={flightPurposes.map((p) => ({
                value: p.purpose_code,
                label: p.purpose_name,
              }))}
              required
            />
          )}

          <DateInput
            label="Flight Date"
            placeholder="Select flight date"
            value={request.flight_date}
            onChange={(date) => handleInputChange("flight_date", date)}
            minDate={new Date()}
            required
          />

          <Group grow>
            <TextInput
              label="Departure Airport"
              placeholder="ICAO code or name"
              value={request.departure_place}
              onChange={(e) =>
                handleInputChange(
                  "departure_place",
                  e.target.value.toUpperCase()
                )
              }
            />
            <TextInput
              label="Arrival Airport"
              placeholder="ICAO code or name"
              value={request.arrival_place}
              onChange={(e) =>
                handleInputChange("arrival_place", e.target.value.toUpperCase())
              }
            />
          </Group>
        </Stack>
      </Card>

      {/* PPR Type & Payment */}
      <Card withBorder padding="lg">
        <Stack spacing="md">
          <Text size="lg" weight={500}>
            PPR Configuration
          </Text>

          <Radio.Group
            label="PPR Type"
            value={request.ppr_type}
            onChange={(value) => handleInputChange("ppr_type", value)}
          >
            <Stack spacing="xs">
              <Radio value="one_time" label="One-Time PPR (30 days validity)" />
              <Radio value="permanent" label="Use Permanent PPR Credits" />
            </Stack>
          </Radio.Group>

          {/* Pricing Display */}
          {pricingLoading && (
            <Alert icon={<Loader size="sm" />} color="blue">
              <Text size="sm">Loading pricing information...</Text>
            </Alert>
          )}

          {pricingError && (
            <Alert icon={<IconAlertCircle />} color="orange">
              <Text size="sm">{pricingError}</Text>
            </Alert>
          )}

          {pricing && !pricingLoading && (
            <Alert
              icon={<IconInfoCircle />}
              color={pricing.price === 0 ? "green" : "blue"}
            >
              <Group position="apart">
                <div>
                  <Text size="sm" weight={500}>
                    Estimated Cost:
                  </Text>
                  {pricing.description && (
                    <Text size="xs" color="dimmed">
                      {pricing.description}
                    </Text>
                  )}
                </div>
                <Badge
                  size="lg"
                  color={pricing.price === 0 ? "green" : "blue"}
                  variant="filled"
                >
                  {pricing.price === 0 ? "FREE" : `EUR ${pricing.price}`}
                </Badge>
              </Group>
            </Alert>
          )}

          {pricing?.price > 0 && request.ppr_type === "one_time" && (
            <Radio.Group
              label="Payment Method"
              value={request.payment_method}
              onChange={(value) => handleInputChange("payment_method", value)}
            >
              <Stack spacing="xs">
                <Radio value="online" label="Pay Online Now" />
                <Radio value="airport" label="Pay at Airport" />
              </Stack>
            </Radio.Group>
          )}
        </Stack>
      </Card>

      {/* Submit Button */}
      <Button
        onClick={submitFlightRequest}
        loading={loading}
        size="lg"
        leftIcon={<IconPlane />}
        disabled={pricingLoading}
      >
        Submit PPR Request
      </Button>

      {/* Success Modal */}
      <Modal
        opened={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Flight Request Successful!"
        centered
        size="md"
      >
        <Stack spacing="md" align="center">
          <IconCheck size={64} color="green" />
          <Text size="xl" weight={700} color="green">
            PPR Created Successfully!
          </Text>

          {pprResult && (
            <Card withBorder style={{ width: "100%" }}>
              <Stack spacing="sm">
                <Group position="apart">
                  <Text weight={500}>PPR Number:</Text>
                  <Group spacing="xs">
                    <Badge size="lg" variant="filled" color="green">
                      {pprResult.ppr_number}
                    </Badge>
                    <CopyButton value={pprResult.ppr_number}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? "Copied" : "Copy PPR Number"}>
                          <ActionIcon
                            color={copied ? "teal" : "gray"}
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck size={16} />
                            ) : (
                              <IconCopy size={16} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                </Group>

                <Group position="apart">
                  <Text>Flight Type:</Text>
                  <Text weight={500}>
                    {request.flight_type.replace("_", " ")}
                  </Text>
                </Group>

                {pprResult.expires_at && (
                  <Group position="apart">
                    <Text>Valid Until:</Text>
                    <Text weight={500}>
                      {new Date(pprResult.expires_at).toLocaleDateString()}
                    </Text>
                  </Group>
                )}

                {pprResult.amount && (
                  <Group position="apart">
                    <Text>Amount:</Text>
                    <Text
                      weight={500}
                      color={
                        parseFloat(pprResult.amount) === 0 ? "green" : "blue"
                      }
                    >
                      {parseFloat(pprResult.amount) === 0
                        ? "FREE"
                        : `EUR ${pprResult.amount}`}
                    </Text>
                  </Group>
                )}

                {pprResult.exemption_applied && (
                  <Group position="apart">
                    <Text>Exemption:</Text>
                    <Badge color="green">Applied</Badge>
                  </Group>
                )}

                {pprResult.payment_required &&
                  request.payment_method === "airport" && (
                    <Alert icon={<IconInfoCircle />} color="orange" size="sm">
                      <Text size="sm">
                        <strong>Payment Required:</strong> Please pay € 
                        {pprResult.amount} at the airport before your flight.
                      </Text>
                    </Alert>
                  )}
              </Stack>
            </Card>
          )}

          <Alert icon={<IconInfoCircle />} color="blue">
            <Text size="sm">
              Your PPR number has been sent to your email address. Please
              include this number in your flight plan when submitting to ATC.
            </Text>
          </Alert>

          <Group>
            <Button
              variant="outline"
              onClick={() => {
                setShowSuccess(false);
                // Reset form for another request
                setRequest({
                  aircraft_registration: "",
                  aircraft_type: "",
                  weight_category: "",
                  selected_pricing_id: null,
                  flight_type: "single_day",
                  purpose_of_flight: "PRIVATE",
                  flight_date: null,
                  departure_place: "",
                  arrival_place: "",
                  ppr_type: "one_time",
                  payment_method: "online",
                });
                setPprResult(null);
                setPricing(null);
              }}
            >
              Submit Another Request
            </Button>
            <Button onClick={() => (window.location.href = "/main")}>
              Go to Dashboard
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};